/**
 * The specialty workspace — `/clinical/$specialty`.
 *
 * rev v11.0.0 · Phase 2 / C3
 *
 * This route is the visible result of the Specialty Registry. It:
 *
 *   1. resolves the specialty from the URL — slug or legacy code;
 *   2. checks the feature flag of THAT specialty, and falls back to the
 *      legacy interface when the flag is off;
 *   3. renders the workspace described by the manifest: its navigation, its
 *      panels, its theme.
 *
 * It knows no specialty by name. The route is the same for the 22
 * workspaces, and a 23rd would work without touching this file.
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { ArrowRight, FileText, FlaskConical, Scan } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SpecialtyShell } from "@/components/specialty/SpecialtyShell";
import { SpecialtyErrorBoundary } from "@/components/specialty/SpecialtyErrorBoundary";
import { useSpecialtyFlag } from "@/hooks/use-specialty-flag";
import {
  getSpecialty,
  resolveSpecialtyRoute,
  shouldRenderWorkspace,
} from "@/lib/specialty";
import { NGAP_CATALOG_HASH } from "@/lib/ngap-catalogue";
import { ROUTES, asRoutePath, type SpecialtyRouteParams } from "@/lib/routes";

/**
 * Where each declared panel leads.
 *
 * The registry declares WHICH panel belongs to a workspace; this table says
 * where its content lives in the application. A panel without an entry
 * renders as "not yet delivered" — explicitly, never as an empty card that
 * looks like a bug.
 */
const PANEL_ROUTES: Record<string, { to: string; label: string }> = {
  VitalSignsGrid: { to: "/patients", label: "Constantes du patient" },
  PatientTimeline: { to: "/patients", label: "Chronologie du patient" },
  PrescriptionList: { to: "/prescription", label: "Ordonnances" },
  LabResultsGrid: { to: "/lab-results", label: "Résultats de laboratoire" },
  ClinicalImageGallery: { to: "/radiology", label: "Imagerie" },
  DicomViewer: { to: "/radiology", label: "Visionneuse DICOM" },
  SeriesNavigator: { to: "/radiology", label: "Séries d'imagerie" },
  EcgViewer: { to: "/patients", label: "Tracé ECG" },
  CardiacRhythm: { to: "/patients", label: "Rythme cardiaque" },
  AiSuggestionPanel: { to: "/ai", label: "Suggestions d'IA" },
  EcgPanel: { to: "/patients", label: "Tracé ECG" },
  PregnancyTracker: { to: "/patients", label: "Suivi de grossesse" },
  GrowthChart: { to: "/patients", label: "Courbes de croissance" },
  PediatricDosage: { to: "/prescription", label: "Posologie pédiatrique" },
  TriageBoard: { to: "/patients", label: "Tableau de triage" },
  ContinuousMonitor: { to: "/patients", label: "Surveillance continue" },
  JointAssessmentPanel: { to: "/patients", label: "Évaluation articulaire" },
  PsychScalePanel: { to: "/consultation", label: "Échelles psychiatriques" },
  GlycemiaPanel: { to: "/lab-results", label: "Glycémie" },
  CoagulationPanel: { to: "/lab-results", label: "Coagulation" },
  RenalPanel: { to: "/lab-results", label: "Bilan rénal" },
  AudiometryPanel: { to: "/patients", label: "Audiométrie" },
  SpirometryPanel: { to: "/patients", label: "Spirométrie" },
  VisualAcuityPanel: { to: "/patients", label: "Acuité visuelle" },
  FundusPanel: { to: "/radiology", label: "Fond d'œil" },
  LesionGrid: { to: "/patients", label: "Lésions cutanées" },
  NeuroExamPanel: { to: "/consultation", label: "Examen neurologique" },
  KineAssessment: { to: "/consultation", label: "Bilan kinésithérapique" },
  SessionList: { to: "/consultation", label: "Séances" },
  RtPlanningPanel: { to: "/radiology", label: "Plan de radiothérapie" },
  DosimetryPanel: { to: "/radiology", label: "Dosimétrie" },
  ValidationQueue: { to: "/lab-results", label: "File de validation" },
};

export const Route = createFileRoute(asRoutePath(ROUTES.specialty))({
  /**
   * Resolve the specialty before rendering.
   *
   * An unknown slug is a 404, not a blank workspace: a typo in a shared link
   * must be visible immediately.
   */
  beforeLoad: ({ params }) => {
    // The route tree is untyped (TD-01), so the params arrive as `never`.
    // The shape is declared in `lib/routes.ts` and is the same one the
    // generated tree would give.
    const { specialty: requested } = params as SpecialtyRouteParams;
    const resolution = resolveSpecialtyRoute(requested);
    if (!resolution.ok) {
      // An unknown slug is a 404, not a blank workspace: a typo in a shared
      // link must be visible immediately. Redirecting to the home page
      // would hide the mistake behind a perfectly normal-looking screen.
      throw notFound();
    }
    return { specialty: resolution.slug };
  },
  component: SpecialtyWorkspace,
});

function SpecialtyWorkspace() {
  // El árbol de rutas generado no declara sus tipos, de modo que
  // `useLoaderData()` devuelve `never`. El valor lo ha resuelto antes el
  // `beforeLoad`, y su presencia está garantizada por esa resolución.
  const loaderData = Route.useLoaderData() as { specialty: string };
  const slugFromLoader = loaderData.specialty;
  const slug = slugFromLoader ?? "";
  const specialty = getSpecialty(slug);
  const flag = useSpecialtyFlag(slug);

  if (!specialty) {
    // Unreachable by construction: `beforeLoad` resolved the parameter
    // before this component ran. It is a guard, not a branch — it must
    // never be a blank screen.
    throw new Error(`specialty '${slug}' is not in the registry`);
  }

  return (
    <SpecialtyErrorBoundary
      specialtyId={specialty.id}
      specialtyLabel={specialty.label}
      legacyHref="/"
    >
      {!shouldRenderWorkspace(flag) ? (
        <LegacyNotice
          slug={specialty.id}
          label={specialty.label}
          degraded={flag.degraded}
          loading={flag.loading}
        />
      ) : (
        <SpecialtyShell specialtyId={specialty.id}>
          <SpecialtyContent specialtyId={specialty.id} />
        </SpecialtyShell>
      )}
    </SpecialtyErrorBoundary>
  );
}

/**
 * The workspace content.
 *
 * The panels are rendered from the manifest; each one links to the screen
 * that actually carries the data. A panel whose screen does not exist yet
 * says so, in words, instead of showing an empty card.
 */
function SpecialtyContent({ specialtyId }: { specialtyId: string }) {
  const specialty = getSpecialty(specialtyId)!;
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {specialty.panels.map((panel) => {
          const target = PANEL_ROUTES[panel.component];
          return (
            <Card key={panel.id}>
              <CardHeader>
                <CardTitle className="text-base">{panel.label}</CardTitle>
                <CardDescription>
                  {target
                    ? "Les données de ce module sont servies par l'écran dédié."
                    : "Ce module n'est pas encore livré."}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {target ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to={target.to}>
                      {target.label}
                      <ArrowRight className="ml-2 h-3.5 w-3.5" />
                    </Link>
                  </Button>
                ) : (
                  <Badge variant="secondary" className="text-[10px]">
                    <FileText className="mr-1 h-3 w-3" />
                    {panel.component}
                  </Badge>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FlaskConical className="h-4 w-4 text-primary" />
            Référentiel de cotation
          </CardTitle>
          <CardDescription>
            {specialty.ngap.catalogueActs > 0
              ? `${specialty.ngap.catalogueActs} actes dans le catalogue NGAP Maroc, mis à jour par le catalogue canonique.`
              : "Aucun acte propre à cette spécialité : ses actes sont cotés dans le périmètre d'une autre spécialité. Le moteur de cotation reste accessible."}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to={asRoutePath("/referential/ngap")}>
              <Scan className="mr-2 h-3.5 w-3.5" />
              Ouvrir le moteur de cotation
            </Link>
          </Button>
          <span className="font-mono text-[10px] text-muted-foreground">
            catalogue {NGAP_CATALOG_HASH.slice(0, 12)}
          </span>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * The message shown when the new experience is not enabled.
 *
 * It is explicit about the reason: the clinician is not left wondering
 * whether they did something wrong.
 */
function LegacyNotice({
  slug,
  label,
  degraded,
  loading,
}: {
  slug: string;
  label: string;
  degraded: boolean;
  loading: boolean;
}) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>L'espace {label} n'est pas encore activé</CardTitle>
          <CardDescription>
            {loading
              ? "Vérification de l'activation de l'espace…"
              : degraded
                ? "Le service d'indicateurs n'a pas répondu : l'interface habituelle reste active par précaution."
                : `Le déploiement de l'espace ${slug} n'inclut pas encore votre établissement.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Le déploiement se fait spécialité par spécialité et établissement
            par établissement. L'interface habituelle reste entièrement
            fonctionnelle.
          </p>
          <Button asChild>
            <Link to={asRoutePath(ROUTES.home)}>Aller à l'interface habituelle</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
